import { useEffect, useState } from "react";
import { ErrorMessage, Form, Formik } from "formik";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { setMessage, useMaterialUIController } from "context";

import {
  Alert,
  AlertTitle,
  Autocomplete,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";

import FormField from "layouts/pages/account/components/FormField";
import FormCategoria from "./FormCategoria";

import { UNIDADEDISTANCIA } from "helpers/constants";

import {
  initialValuesCategoria,
  initialValuesEditCategoria,
} from "./schemas/initialValuesCategoria";
import { validationDistancia } from "./schemas/validations";
import formFieldDistancia from "./schemas/formFieldDistancia";

import {
  IEditCategoria,
  IEditDistancia,
  IEventoResponse,
} from "interfaces/eventos";
import { createCategoriaDistancia, editDistancia } from "services/evento";
import { SelectedDistanciaProps } from ".";

const listUnidade = UNIDADEDISTANCIA;

interface Props {
  evento: IEventoResponse;
  handleClose: () => void;
  updateEvento: () => void;
  selectedDistancia: SelectedDistanciaProps;
}

export default function FormDistancia({
  evento,
  handleClose,
  updateEvento,
  selectedDistancia,
}: Props) {
  const [controller, dispatch] = useMaterialUIController();
  const { dataConfig } = controller;
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [valuesForm, setValuesForms] = useState(null);
  const [listCategorias, setListCategorias] = useState<IEditCategoria[]>([]);

  const { descricao, distancia, unidade, qtdLimite, incluiCamiseta } =
    formFieldDistancia;
  const { limiteInscricao, lotesEventos } = evento;

  useEffect(() => {
    if (selectedDistancia) {
      setIsEdit(selectedDistancia.type === "edit");
      setValuesForms(initialValuesEditCategoria(selectedDistancia.distCat));
      const categoria = selectedDistancia.distCat.categoriaEvento.map((cat) => {
        const categorias: IEditCategoria = {
          id: cat.id,
          descricao: cat.descricao,
          idadeInicial: cat.idadeInicial,
          idadefinal: cat.idadefinal,
          qtdMinima: cat.qtdMinima,
          sexoId: getSexoId(cat.sexo?.tipo),
          tagId: getTagId(cat.tag?.tipo),
        };
        return categorias;
      });
      setListCategorias(categoria);
    } else {
      setValuesForms(initialValuesCategoria);
    }
  }, []);

  function getSexoId(targetSexo?: string): number {
    return (
      dataConfig.tipoSexo.find((item: any) => item.tipo === targetSexo)?.id ?? 0
    );
  }

  function getTagId(targetTag?: string): number {
    return (
      dataConfig.tipoTags.find((item: any) => item.tipo === targetTag)?.id ?? 0
    );
  }

  const existTagsNull = () => {
    const existTag = listCategorias.some(
      (cat) =>
        !lotesEventos[0].valoresTags.some((tag) => tag.tag.id === cat.tagId)
    );
    return existTag;
  };

  const handlerSubmit = (values: any) => {
    // TODO: fazer o cadastro das categorias
    // Verificar se existe pelo menos uma distância definida
    if (listCategorias.length > 0) {
      if (isEdit) {
        const dataEditDistancia: IEditDistancia = {
          id: selectedDistancia.distCat.id,
          ...values,
        };

        // EDITAR: DISTANCIA
        editDistancia(dataEditDistancia)
          .then(() => (handleClose(), updateEvento()))
          .catch((error: any) =>
            setMessage(dispatch, {
              open: true,
              text: error.message,
              severity: "error",
            })
          );
      } else {
        const dataCategoriaDistancia = {
          ...values,
          eventoId: evento.id,
          categorias: listCategorias,
        };
        // NOVA: DISTANCIA e CATEGORIAS
        createCategoriaDistancia(dataCategoriaDistancia)
          .then(() => (handleClose(), updateEvento()))
          .catch((error: any) =>
            setMessage(dispatch, {
              open: true,
              text: error.message,
              severity: "error",
            })
          );
      }
    } else {
      setMessage(dispatch, {
        open: true,
        text: "Não existe categoria definida",
        severity: "error",
      });
    }
  };

  return (
    <MDBox
      bgColor="grey-100"
      border="solid 1px grey"
      borderRadius="lg"
      sx={{ my: 2, p: 3 }}
    >
      {valuesForm && (
        <Formik
          initialValues={valuesForm}
          validationSchema={validationDistancia}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false);
            handlerSubmit(values);
          }}
        >
          {({ values, handleChange, setFieldValue }) => {
            return (
              <Form id="form-distancia">
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Distância
                  </Typography>

                  <MDButton
                    variant="gradient"
                    disabled={existTagsNull()}
                    color={isEdit ? "info" : "success"}
                    size="medium"
                    type="submit"
                  >
                    {isEdit ? "Salvar Alteração" : "Salvar"}
                  </MDButton>
                </MDBox>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <FormField
                      variant="outlined"
                      name={descricao.name}
                      type={descricao.type}
                      label={descricao.label}
                      required
                      value={values.descricao}
                      onChange={handleChange}
                      onBlur={handleChange}
                      sx={{ background: "white" }}
                    />
                    <MDTypography
                      component="div"
                      variant="caption"
                      color="error"
                      fontWeight="regular"
                    >
                      {(<ErrorMessage name={descricao.name} />) as any}
                    </MDTypography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormField
                      variant="outlined"
                      name={distancia.name}
                      type={distancia.type}
                      label={distancia.label}
                      value={values.distancia}
                      required
                      onChange={handleChange}
                      onBlur={handleChange}
                      sx={{ background: "white" }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Autocomplete
                      value={values.unidade}
                      options={listUnidade}
                      onChange={(event, newValue) => {
                        setFieldValue("unidade", newValue); // Certifique-se de passar o novo valor
                      }}
                      getOptionLabel={(option) => option}
                      renderInput={(params) => (
                        <FormField
                          variant="outlined"
                          {...params}
                          // value={values.unidade} // controlando o valor aqui também
                          name={unidade.name}
                          required
                          label={unidade.label}
                          InputLabelProps={{ shrink: true }}
                          sx={{ background: "white" }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormField
                      variant="outlined"
                      name={qtdLimite.name}
                      maximum={limiteInscricao}
                      type={qtdLimite.type}
                      label={qtdLimite.label}
                      value={values.qtdLimite}
                      onChange={handleChange}
                      required
                      error={Number(values.qtdLimite) > Number(limiteInscricao)}
                      sx={{ background: "white" }}
                      inputProps={{ min: 1, max: limiteInscricao }}
                    />
                    <MDTypography variant="caption">
                      Limite de inscrição do evento: <b>{limiteInscricao}</b>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.incluiCamiseta !== false}
                          onChange={(event) =>
                            setFieldValue(
                              incluiCamiseta.name,
                              event.target.checked
                            )
                          }
                        />
                      }
                      label={incluiCamiseta.label}
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ ml: 6, mt: -0.5 }}
                    >
                      Quando desligado, a inscrição nesta distância não pede
                      tamanho de camiseta (ex.: kit básico).
                    </MDTypography>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      )}

      {existTagsNull() && (
        <Alert
          variant="filled"
          severity="error"
          sx={{
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.05)" }, // Define o ponto máximo do pulso
              "100%": { transform: "scale(1)" },
            },
          }}
        >
          <AlertTitle>Atenção</AlertTitle>
          Existe categorias sem Valor, defina uma TAG para categorias.
        </Alert>
      )}

      <FormCategoria
        evento={evento}
        listCategorias={listCategorias}
        handleSelectedListCategoria={setListCategorias}
        isEdit={isEdit}
        distanciaId={selectedDistancia?.distCat?.id}
      />
    </MDBox>
  );
}
